import { getApiBaseUrl } from '@/services/Http';

/**
 * Service for handling attachment/file operations
 * Provides environment-aware URL generation for file downloads and uploads
 */
class AttachmentService {
    /**
     * Get the download URL for an attachment
     * @param attachmentId - The GUID of the attachment
     * @returns Full URL to download the file
     */
    public getDownloadUrl(attachmentId: string): string {
        const baseUrl = getApiBaseUrl();
        return `${baseUrl}/Data/Download/${attachmentId}`;
    }

    /**
     * Get the upload endpoint URL
     * @returns Full URL for file upload endpoint
     */
    public getUploadUrl(): string {
        const baseUrl = getApiBaseUrl();
        return `${baseUrl}/Data/Upload`;
    }

    /**
     * Get the delete endpoint URL
     * @param attachmentId - The GUID of the attachment to delete
     * @returns Full URL for delete endpoint
     */
    public getDeleteUrl(attachmentId: string): string {
        const baseUrl = getApiBaseUrl();
        return `${baseUrl}/Data/Delete/${attachmentId}`;
    }

    /**
     * Get the list endpoint URL
     * @returns Full URL for list endpoint
     */
    public getListUrl(): string {
        const baseUrl = getApiBaseUrl();
        return `${baseUrl}/Data/List`;
    }

    /**
     * Open a file in a new browser tab
     * @param attachmentId - The GUID of the attachment
     */
    public openInNewTab(attachmentId: string): void {
        const url = this.getDownloadUrl(attachmentId);
        window.open(url, '_blank');
    }

    /**
     * Check if an attachment is a finalized PDF
     * @param attachment - The attachment object
     * @returns true if the attachment has Extra='finalized'
     */
    public isFinalizedPDF(attachment: any): boolean {
        return attachment?.Extra === 'finalized';
    }

    /**
     * Find the finalized PDF in a list of attachments
     * @param attachments - Array of attachment objects
     * @returns The finalized PDF attachment or null if not found
     */
    public findFinalizedPDF(attachments: any[]): any | null {
        return attachments.find(f => this.isFinalizedPDF(f)) || null;
    }
}

const singleton = new AttachmentService();
export default singleton;
